// Tenantra Inc. - Data Protection Policy (TransUnion-style industry standard formatting)
import { ArrowLeft, Building2, Lock, Printer } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useState, useRef } from 'react';

const TOC_ITEMS = [
  { id: 'data-protection-notice', label: 'Data Protection Notice' },
  { id: 'our-commitment', label: '1. Our Commitment' },
  { id: 'data-protection-principles', label: '2. Data Protection Principles' },
  { id: 'nature-of-platform-data', label: '3. Nature of Platform Data' },
  { id: 'security-measures', label: '4. Security Measures' },
  { id: 'data-breach-notification', label: '5. Data Breach Notification' },
  { id: 'data-location-transfers', label: '6. Data Location & Transfers' },
  { id: 'sub-processors', label: '7. Sub-Processors' },
  { id: 'dispute-correction', label: '8. Dispute & Data Correction' },
  { id: 'employee-training', label: '9. Employee Training & Access' },
  { id: 'your-rights', label: '10. Your Rights' },
  { id: 'limitation-responsibility', label: '11. Limitation of Responsibility' },
  { id: 'data-retention', label: '12. Data Retention Schedule' },
  { id: 'supporting-documentation', label: '13. Supporting Documentation' },
  { id: 'privacy-officer', label: '14. Privacy Officer Contact' },
  { id: 'privacy-commissioner', label: '15. Privacy Commissioner' },
  { id: 'policy-updates', label: '16. Policy Updates' },
];

export default function DataProtectionPage() {
  const [activeSection, setActiveSection] = useState('');
  const contentRef = useRef(null);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-40 print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3.5">
          <div className="flex items-center justify-between">
            <Logo size="md" />
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="text-xs text-stone-500 hover:text-stone-700 flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-lg hover:bg-stone-50 transition-colors"
                data-testid="print-btn"
              >
                <Printer className="w-3.5 h-3.5" />
                Print
              </button>
              <a href="/" className="text-sm text-stone-500 hover:text-teal-600 flex items-center gap-1.5 transition-colors" data-testid="back-home-link">
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Home</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Main layout */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10 print:py-4 print:max-w-none">
        <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-10">

          {/* Sidebar TOC */}
          <aside className="hidden lg:block print:hidden">
            <nav className="sticky top-20">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400 mb-3">On this page</p>
              <ul className="space-y-0.5 max-h-[calc(100vh-120px)] overflow-y-auto pr-1">
                {TOC_ITEMS.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className={`block w-full text-left text-[13px] leading-snug py-1.5 px-2.5 rounded transition-colors ${
                        activeSection === item.id
                          ? 'text-teal-700 bg-teal-50 font-medium'
                          : 'text-stone-500 hover:text-stone-800 hover:bg-stone-50'
                      }`}
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Document body */}
          <article ref={contentRef}>
            {/* Title block */}
            <div className="mb-8 sm:mb-10 pb-6 border-b border-stone-200">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-teal-600" />
                <span className="text-xs font-semibold uppercase tracking-wider text-teal-600">Legal</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-2" data-testid="data-protection-title">Data Protection Policy</h1>
              <p className="text-sm text-stone-500">(Last updated April 2026 &mdash; Version 2.0)</p>
            </div>

            {/* ===== DATA PROTECTION NOTICE ===== */}
            <section id="data-protection-notice" className="mb-10 scroll-mt-24">
              <div className="bg-stone-50 border border-stone-200 rounded-lg overflow-hidden">
                <div className="bg-stone-800 px-5 py-3">
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Tenantra Inc. Data Protection Notice</h2>
                </div>
                <div className="px-5 py-5 space-y-5">
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Scope</h3>
                    <p className="text-sm text-stone-700 leading-relaxed">
                      This Data Protection Policy outlines Tenantra's commitment to data security, the technical and organizational measures we implement, and your rights regarding your personal information.
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Key Safeguards</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>TLS 1.3 encryption in transit, AES-256 at rest</li>
                      <li>Role-based access controls and multi-factor authentication</li>
                      <li>Canadian-based data centres (Toronto, Montreal)</li>
                      <li>24/7 security monitoring with intrusion detection</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Your Rights</h3>
                    <ul className="text-sm text-stone-700 space-y-1 list-disc pl-5">
                      <li>Access, correct, or request deletion of your data</li>
                      <li>Dispute any rental report submitted about you</li>
                      <li>File complaints with our Privacy Officer or the Privacy Commissioner of Canada</li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xs font-bold uppercase tracking-wider text-stone-500 mb-1.5">Contact</h3>
                    <p className="text-sm text-stone-700">
                      Privacy Officer, Tenantra Inc. &mdash; Email: <a href="mailto:privacy@tenantra.ca" className="text-teal-700 underline">privacy@tenantra.ca</a>
                    </p>
                  </div>
                  <p className="text-xs text-stone-500 pt-2 border-t border-stone-200">
                    For full details, please review the complete Data Protection Policy below.
                  </p>
                </div>
              </div>
            </section>

            {/* ===== FULL POLICY ===== */}

            {/* 1. Our Commitment */}
            <section id="our-commitment" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">1. Our Commitment</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra Inc. ("<strong>Tenantra</strong>") is committed to protecting the personal information entrusted to us by landlords, tenants, and users of our platform.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                We comply with the <em>Personal Information Protection and Electronic Documents Act</em> (PIPEDA) and applicable provincial privacy legislation. Our data protection practices are designed to meet or exceed the requirements of Canadian privacy law.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed">
                This Data Protection Policy outlines our commitment to data security, the measures we implement, and your rights regarding your personal information.
              </p>
            </section>

            {/* 2. Data Protection Principles */}
            <section id="data-protection-principles" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">2. Data Protection Principles</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">We adhere to the following data protection principles:</p>

              {[
                { title: 'Accountability', desc: 'We are responsible for personal information under our control. Our Privacy Officer oversees compliance with this policy.' },
                { title: 'Identifying Purposes', desc: 'We identify the purposes for collecting personal information before or at the time of collection.' },
                { title: 'Consent', desc: 'We obtain meaningful consent for the collection, use, and disclosure of personal information.' },
                { title: 'Limiting Collection', desc: 'We collect only the personal information necessary for identified purposes.' },
                { title: 'Limiting Use, Disclosure, and Retention', desc: 'We use personal information only for identified purposes and retain it only as long as necessary.' },
                { title: 'Safeguards', desc: 'We protect personal information with appropriate security measures.' },
                { title: 'Openness', desc: 'We make our policies and practices readily available.' },
                { title: 'Individual Access', desc: 'We provide individuals access to their personal information upon request.' },
                { title: 'Challenging Compliance', desc: 'Individuals may challenge our compliance with these principles.' },
              ].map((item, i) => (
                <div key={i} className="mb-4 last:mb-0">
                  <h3 className="text-sm font-bold text-stone-800 mb-1">{item.title}</h3>
                  <p className="text-sm text-stone-700 leading-relaxed">{item.desc}</p>
                </div>
              ))}

              <div className="mt-5 mb-0">
                <h3 className="text-sm font-bold text-stone-800 mb-1">Accuracy</h3>
                <p className="text-sm text-stone-700 leading-relaxed mb-2">
                  We implement processes designed to support data accuracy, including identity verification, consent-based onboarding, and dispute mechanisms.
                </p>
                <div className="bg-stone-50 border-l-4 border-stone-800 px-4 py-3">
                  <p className="text-sm text-stone-800 font-semibold">
                    However, Tenantra does not guarantee the absolute accuracy, completeness, or reliability of user-submitted information.
                  </p>
                </div>
              </div>
            </section>

            {/* 3. Nature of Platform Data */}
            <section id="nature-of-platform-data" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">3. Nature of Platform Data and Role of Tenantra</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra operates as a platform that facilitates identity-verified, consent-based rental history records submitted by landlords.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Information on the platform includes user-submitted data relating to rental relationships and post-tenancy reporting.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Tenantra:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>Does not independently verify all submitted information</li>
                <li>Does not act as a government registry, land title authority, or credit reporting agency</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                Data on the platform reflects submissions made by verified landlord accounts within a structured system that includes tenant consent and dispute mechanisms.
              </p>
            </section>

            {/* 4. Security Measures */}
            <section id="security-measures" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">4. Security Measures</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">We implement comprehensive technical and organizational security measures:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Encryption</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>TLS 1.3 for all data in transit</li>
                <li>AES-256 encryption for data at rest</li>
                <li>Encrypted database connections</li>
                <li>Secure key management practices</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Access Controls</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Role-based access control (RBAC)</li>
                <li>Multi-factor authentication (MFA)</li>
                <li>Unique user credentials</li>
                <li>Automatic session timeout</li>
                <li>Privileged access management</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Infrastructure Security</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Canadian-based data centres</li>
                <li>SOC 2 Type II certified hosting</li>
                <li>Redundant systems and backups</li>
                <li>DDoS protection</li>
                <li>Web application firewall (WAF)</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Monitoring and Detection</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>24/7 security monitoring</li>
                <li>Intrusion detection systems</li>
                <li>Security information and event management (SIEM)</li>
                <li>Regular vulnerability scanning</li>
                <li>Penetration testing (annual)</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Physical Security</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Biometric access controls</li>
                <li>Video surveillance</li>
                <li>Environmental controls</li>
                <li>Secure disposal of hardware</li>
              </ul>
            </section>

            {/* 5. Data Breach Notification */}
            <section id="data-breach-notification" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">5. Data Breach Notification</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">In the event of a data breach, we follow a structured response process:</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Detection and Assessment</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Immediate investigation upon detection</li>
                <li>Assessment of breach scope and impact</li>
                <li>Determination of affected individuals</li>
                <li>Evaluation of risk of significant harm</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Containment</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Immediate steps to stop the breach</li>
                <li>Preservation of evidence</li>
                <li>System isolation if necessary</li>
                <li>Engagement of incident response team</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Notification (if required)</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Under PIPEDA, we will notify:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Affected individuals as soon as feasible where there is a real risk of significant harm</li>
                <li>The Privacy Commissioner of Canada</li>
                <li>Third parties where notification may reduce risk</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Notification Contents</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Description of the breach</li>
                <li>Types of information involved</li>
                <li>Steps taken to reduce harm</li>
                <li>Contact information</li>
                <li>Complaint process information</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Record Keeping</h3>
              <p className="text-sm text-stone-700 leading-relaxed">
                We maintain records of all breaches for at least 24 months.
              </p>
            </section>

            {/* 6. Data Location and Transfers */}
            <section id="data-location-transfers" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">6. Data Location and Transfers</h2>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Data Residency</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-5">
                All personal information collected by Tenantra is stored on servers located in Canada. Our primary data centre is in Toronto, Ontario, with disaster recovery in Montreal, Quebec.
              </p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Cross-Border Transfers</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">
                We do not routinely transfer personal information outside of Canada. In limited cases, we ensure:
              </p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Comparable protection levels</li>
                <li>Contractual safeguards</li>
                <li>Compliance with PIPEDA</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Third-Party Processors</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Our service providers include:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>AWS Canada (hosting)</li>
                <li>MongoDB Atlas (database)</li>
                <li>Postmark (email)</li>
                <li>Vonage (SMS)</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed mt-2">
                All providers are bound by data protection agreements.
              </p>
            </section>

            {/* 7. Sub-Processors */}
            <section id="sub-processors" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">7. Sub-Processors</h2>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Identity Verification</h3>
              <p className="text-sm text-stone-700 leading-relaxed mb-1">Didit (Didit Identity Verification) &mdash; identity verification</p>
              <p className="text-xs text-stone-500 mb-5">(Data: government ID, selfie, verification results)</p>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Communications</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Postmark &mdash; email delivery</li>
                <li>Vonage &mdash; SMS verification</li>
              </ul>

              <h3 className="text-sm font-bold text-stone-800 mb-2">Infrastructure</h3>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-3">
                <li>AWS Canada</li>
                <li>MongoDB Atlas</li>
              </ul>

              <p className="text-sm text-stone-700 leading-relaxed">
                We update this list when changes occur.
              </p>
            </section>

            {/* 8. Dispute and Data Correction */}
            <section id="dispute-correction" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">8. Dispute and Data Correction Framework</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                Tenants have the right to dispute rental reports they believe are inaccurate, misleading, or incomplete.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">When a dispute is raised:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-4">
                <li>The report may be flagged</li>
                <li>Supporting information may be reviewed</li>
                <li>Appropriate action may be taken, including updating, restricting, or removing content</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra reserves the right to determine outcomes based on available information and platform policies.
              </p>
              <div className="bg-stone-50 border-l-4 border-teal-600 px-4 py-3">
                <p className="text-sm text-stone-800 font-semibold">
                  The dispute process is a core safeguard designed to maintain fairness and data integrity.
                </p>
              </div>
            </section>

            {/* 9. Employee Training and Access */}
            <section id="employee-training" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">9. Employee Training and Access</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">All employees receive:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-5">
                <li>Privacy training</li>
                <li>Security awareness training</li>
                <li>Role-based access restrictions</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Access is:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5">
                <li>Limited to necessary functions</li>
                <li>Logged and auditable</li>
                <li>Revoked immediately upon termination</li>
              </ul>
            </section>

            {/* 10. Your Rights */}
            <section id="your-rights" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">10. Your Rights</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">You have the right to:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-3">
                <li><strong>Access</strong> your data</li>
                <li><strong>Request corrections</strong> to inaccurate or incomplete information</li>
                <li><strong>Withdraw consent</strong> for certain uses of your personal information</li>
                <li><strong>File complaints</strong> with our Privacy Officer or the Office of the Privacy Commissioner of Canada</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                Requests are processed within required timelines under applicable law.
              </p>
            </section>

            {/* 11. Limitation of Platform Responsibility */}
            <section id="limitation-responsibility" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">11. Limitation of Platform Responsibility</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                Tenantra is not responsible for decisions made by landlords or third parties based on information obtained through the platform.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed mb-3">
                The platform provides structured rental history records but does not provide legal, financial, or tenancy advice.
              </p>
              <p className="text-sm text-stone-700 leading-relaxed font-medium">
                Users are responsible for how they interpret and use platform data.
              </p>
            </section>

            {/* 12. Data Retention Schedule */}
            <section id="data-retention" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">12. Data Retention Schedule</h2>
              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-stone-50">
                      <th className="text-left px-4 py-2.5 font-bold text-stone-800 border-b border-stone-200">Data Category</th>
                      <th className="text-left px-4 py-2.5 font-bold text-stone-800 border-b border-stone-200">Retention Period</th>
                    </tr>
                  </thead>
                  <tbody className="text-stone-700">
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Active accounts</td>
                      <td className="px-4 py-2.5">Duration of account</td>
                    </tr>
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Inactive accounts</td>
                      <td className="px-4 py-2.5">2 years</td>
                    </tr>
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Deleted accounts</td>
                      <td className="px-4 py-2.5">30 days</td>
                    </tr>
                    <tr className="border-b border-stone-100">
                      <td className="px-4 py-2.5">Rental reports</td>
                      <td className="px-4 py-2.5">Up to 7 years</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5">Disputed reports</td>
                      <td className="px-4 py-2.5">7 years after resolution</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-sm text-stone-700 leading-relaxed mt-3">
                Data is securely deleted after retention periods expire.
              </p>
            </section>

            {/* 13. Supporting Documentation */}
            <section id="supporting-documentation" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">13. Supporting Documentation</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-2">Documents uploaded to Tenantra are used for:</p>
              <ul className="list-disc pl-5 text-sm text-stone-700 space-y-1.5 mb-3">
                <li>Internal records</li>
                <li>Audit</li>
                <li>Dispute resolution</li>
              </ul>
              <p className="text-sm text-stone-700 leading-relaxed">
                They are not independently verified as proof of ownership or legal title.
              </p>
            </section>

            {/* 14. Privacy Officer Contact */}
            <section id="privacy-officer" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">14. Privacy Officer Contact</h2>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-700">
                <p className="font-bold text-stone-800 mb-1">Privacy Officer</p>
                <p>Tenantra Inc.</p>
                <p className="mt-2">Email: <a href="mailto:privacy@tenantra.ca" className="text-teal-700 underline">privacy@tenantra.ca</a></p>
              </div>
            </section>

            {/* 15. Office of the Privacy Commissioner */}
            <section id="privacy-commissioner" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">15. Office of the Privacy Commissioner</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                You may contact the Office of the Privacy Commissioner of Canada if you are not satisfied with our response to your inquiry or complaint.
              </p>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-5 text-sm text-stone-700">
                <p className="font-bold text-stone-800 mb-1">Office of the Privacy Commissioner of Canada</p>
                <p>30 Victoria Street, Gatineau, Quebec K1A 1H3</p>
                <p>Toll-free: 1-800-282-1376</p>
                <p>Website: <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">www.priv.gc.ca</a></p>
              </div>
            </section>

            {/* 16. Policy Updates */}
            <section id="policy-updates" className="mb-9 scroll-mt-24">
              <h2 className="text-lg font-bold text-stone-900 mb-3 pb-2 border-b border-stone-200">16. Policy Updates</h2>
              <p className="text-sm text-stone-700 leading-relaxed mb-4">
                We may update this policy periodically to reflect changes in our practices, services, or applicable legal requirements.
              </p>
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 text-sm text-stone-700">
                <p className="font-bold text-stone-800 mb-1">Version Information</p>
                <p>Version: 2.0</p>
                <p>Last updated: April 2026</p>
              </div>
            </section>

            {/* Document footer */}
            <div className="pt-6 mt-6 border-t border-stone-200 text-center">
              <p className="text-xs text-stone-400">
                &copy; {new Date().getFullYear()} Tenantra Inc. All rights reserved. &mdash; PIPEDA Compliant.
              </p>
            </div>
          </article>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print\\:hidden { display: none !important; }
          @page { margin: 0.75in; size: letter; }
        }
      `}</style>
    </div>
  );
}
